import Konva from 'konva';
import * as state from './state.js';
import { tools as toolMap } from './tools/index.js';
import { computeLetterbox } from './letterbox.js';
import { EndpointHandles } from './endpoint-handles.js';
import { StrokeSelection } from './stroke-selection.js';

// Shape kinds that swap the AABB transformer for an endpoint-handle overlay
// (CleanShot-style tight selection on 2-point shapes).
const ENDPOINT_KINDS = new Set(['line', 'arrow']);
// Freehand strokes get a path-hugging selection halo instead of a bbox, since
// they have no meaningful resize handles. (Highlight is a rectangle, not a
// path — it stays on the standard transformer.)
const STROKE_KINDS = new Set(['pencil']);

export class Engine {
  constructor({ container, backgroundDataUrl, width, height }) {
    this.container = container;
    this.backgroundDataUrl = backgroundDataUrl;

    this.layout = {
      innerW: width || (container?.clientWidth ?? 0),
      innerH: height || (container?.clientHeight ?? 0),
      naturalW: 0,
      naturalH: 0,
      ox: 0, oy: 0, dw: 0, dh: 0,
    };

    this.stage = new Konva.Stage({
      container,
      width: this.layout.innerW,
      height: this.layout.innerH,
    });

    this.bgLayer = new Konva.Layer({ listening: false });
    // Screenshot-modifying annotations (pixelate, blur, blackout) live on
    // their own layer so the highlight scrim — which lives on contentLayer —
    // can dim them like any other piece of the screenshot. Keeping them on
    // contentLayer would render them ABOVE the scrim, making blurred regions
    // look "highlighted" against a dimmed page.
    this.bgEffectsLayer = new Konva.Layer();
    this.contentLayer = new Konva.Layer();
    this.uiLayer = new Konva.Layer();
    this.stage.add(this.bgLayer, this.bgEffectsLayer, this.contentLayer, this.uiLayer);

    this.transformer = new Konva.Transformer({
      anchorFill: '#FFFFFF',
      anchorStroke: '#155EEF',
      anchorStrokeWidth: 1.5,
      anchorSize: 10,
      anchorCornerRadius: 5,
      borderStroke: '#155EEF',
      borderStrokeWidth: 1.5,
      rotateEnabled: false,
      keepRatio: false,
      ignoreStroke: true,
      padding: 4,
    });
    this.uiLayer.add(this.transformer);

    this.endpointHandles = new EndpointHandles(this);
    this.strokeSelection = new StrokeSelection(this);

    // Tiny event bus so tools can signal lifecycle events (e.g. bug-pin
    // placement) up to the host UI without coupling tools to the Vue layer.
    this._listeners = new Map();

    // Most-recently-placed bug pin's coordinates, captured in both source-
    // pixel and normalized form for AI agents reading the screenshot.
    this._pinCoords = null;

    this.bgImage = null;
    this._screenshotImg = null;
    this._undoStack = [];
    this._redoStack = [];
    this._activeToolHandle = null;

    this._onPointerDown = this._onPointerDown.bind(this);
    this._onPointerMove = this._onPointerMove.bind(this);
    this._onPointerUp = this._onPointerUp.bind(this);

    this.stage.on('pointerdown', this._onPointerDown);
    this.stage.on('pointermove', this._onPointerMove);
    this.stage.on('pointerup', this._onPointerUp);

    this._onKeyDown = this._onKeyDown.bind(this);

    // Live property updates: when the toolbar tweaks color / strokeWidth /
    // tool defaults and there's a selected node, dispatch the change to
    // the node's owning tool so the user sees the edit reflected on the
    // existing shape (CleanShot-style).
    this._propUnsubs = [
      state.subscribe('selectedColor', (v) => this._applyToSelection('color', v)),
      state.subscribe('strokeWidth', (v) => this._applyToSelection('strokeWidth', v)),
      state.subscribe('toolDefaults', (defaults) => this._applyToolDefaultsToSelection(defaults)),
    ];
  }

  _applyToSelection(prop, value) {
    const node = state.get('selectedNode');
    if (!node || !node._kind) return;
    const tool = toolMap[node._kind];
    if (!tool?.apply) return;
    tool.apply(node, prop, value);
    // Redraw whichever layer the node lives on — most tools live on
    // contentLayer, but blur lives on bgEffectsLayer.
    node.getLayer()?.batchDraw();
  }

  _applyToolDefaultsToSelection(defaults) {
    const node = state.get('selectedNode');
    if (!node || !node._kind) return;
    const tool = toolMap[node._kind];
    if (!tool?.apply) return;
    const toolDefaults = defaults[node._kind];
    if (!toolDefaults) return;
    // Re-apply every property from this tool's defaults. Idempotent — re-
    // setting an unchanged value is a no-op cost, and we don't have to track
    // which specific key flipped.
    for (const [key, val] of Object.entries(toolDefaults)) {
      tool.apply(node, key, val);
    }
    // Group bounds can shift when an apply() swaps in/out a bg rect or
    // changes padding/border (e.g. text preset toggle). The transformer
    // doesn't re-measure on its own — nudge it.
    if (this.transformer.nodes().includes(node)) this.transformer.forceUpdate();
    if (this.endpointHandles.node === node) this.endpointHandles.refresh();
    if (this.strokeSelection.node === node) this.strokeSelection.refresh();
    // Same as above — redraw the layer the changed node actually lives on.
    node.getLayer()?.batchDraw();
  }

  async load() {
    if (!this.backgroundDataUrl) return;
    const img = await loadImage(this.backgroundDataUrl);
    this._screenshotImg = img;
    this.layout.naturalW = img.naturalWidth;
    this.layout.naturalH = img.naturalHeight;
    this._applyLayout();

    this.bgImage = new Konva.Image({
      image: img,
      x: this.layout.ox,
      y: this.layout.oy,
      width: this.layout.dw,
      height: this.layout.dh,
      listening: false,
    });
    this.bgLayer.add(this.bgImage);
    this.bgLayer.draw();
  }

  resize(innerW, innerH) {
    this.layout.innerW = innerW;
    this.layout.innerH = innerH;
    this.stage.size({ width: innerW, height: innerH });
    this._applyLayout();
    if (this.bgImage) {
      this.bgImage.setAttrs({
        x: this.layout.ox,
        y: this.layout.oy,
        width: this.layout.dw,
        height: this.layout.dh,
      });
    }
    this.stage.batchDraw();
  }

  _applyLayout() {
    const { naturalW, naturalH, innerW, innerH } = this.layout;
    if (!naturalW || !naturalH) return;
    const box = computeLetterbox(naturalW, naturalH, innerW, innerH);
    Object.assign(this.layout, box);
  }

  attachKeyboard(target = window) {
    this._keyTarget = target;
    target.addEventListener('keydown', this._onKeyDown);
  }

  detachKeyboard() {
    if (this._keyTarget) {
      this._keyTarget.removeEventListener('keydown', this._onKeyDown);
      this._keyTarget = null;
    }
  }

  _onKeyDown(e) {
    const meta = e.metaKey || e.ctrlKey;
    if (meta && e.key.toLowerCase() === 'z') {
      e.preventDefault();
      if (e.shiftKey) this.redo(); else this.undo();
      return;
    }
    if (e.key === 'Escape') {
      // Marks the key handled only when it dropped a selection, so the host
      // can use an unhandled Escape to close the annotator.
      if (state.get('selectedNode')) {
        e.preventDefault();
        this.deselect();
      }
      return;
    }
    if (e.key === 'Delete' || e.key === 'Backspace') {
      this.deleteSelection();
    }
  }

  _onPointerDown(e) {
    // Clicks on blank canvas (the stage itself or the background image) always
    // deselect, regardless of which tool is active. This replaces the previous
    // select-tool-only behavior so users no longer need to switch tools just
    // to drop a selection.
    const onBlankCanvas = e.target === this.stage || e.target === this.bgImage;
    if (onBlankCanvas) {
      this.deselect();
    }
    // Only fire the active tool's onPointerDown for blank-canvas clicks.
    // Clicks that land on existing annotations or on UI (transformer anchors,
    // endpoint handles) shouldn't accidentally place a new shape — those
    // handlers manage their own behavior via cancelBubble. Without this, e.g.
    // grabbing a transformer corner of a number marker also dropped a fresh
    // numbered marker at that spot.
    if (!onBlankCanvas) return;
    const tool = this._currentTool();
    if (tool?.onPointerDown) tool.onPointerDown(this, e);
  }

  // Override the stage cursor (used when hovering over an interactive node).
  // Pass `null` to clear the override and let the host's CSS cursor apply.
  // Uses `setProperty(..., 'important')` because shells (e.g. the extension's
  // prefixed Tailwind config) compile utilities with `!important`, which a
  // plain inline style cannot beat.
  setCursor(value) {
    const el = this.stage.container();
    if (!el) return;
    if (value == null) {
      el.style.removeProperty('cursor');
    } else {
      el.style.setProperty('cursor', value, 'important');
    }
  }

  _onPointerMove(e) {
    const tool = this._currentTool();
    if (tool?.onPointerMove) tool.onPointerMove(this, e);
  }

  _onPointerUp(e) {
    const tool = this._currentTool();
    if (tool?.onPointerUp) tool.onPointerUp(this, e);
  }

  _currentTool() {
    return toolMap[state.get('activeTool')] ?? null;
  }

  pushOp(op) {
    this._undoStack.push(op);
    this._redoStack.length = 0;
    // Signal to host UI that the user has produced at least one annotation
    // action. Used to gate UI affordances like "Next" — there's no reason to
    // let a user advance without marking anything. Fires for every op, not
    // just the first; consumers should debounce/latch on their side.
    this.emit('op-pushed', op);
  }

  undo() {
    const op = this._undoStack.pop();
    if (!op) return;
    op.undo(this);
    this._redoStack.push(op);
    this.contentLayer.batchDraw();
  }

  redo() {
    const op = this._redoStack.pop();
    if (!op) return;
    op.redo(this);
    this._undoStack.push(op);
    this.contentLayer.batchDraw();
  }

  on(event, fn) {
    if (!this._listeners.has(event)) this._listeners.set(event, new Set());
    this._listeners.get(event).add(fn);
    return () => this._listeners.get(event)?.delete(fn);
  }

  emit(event, payload) {
    const set = this._listeners.get(event);
    if (!set) return;
    for (const fn of set) fn(payload);
  }

  setPinCoordinates(coords) { this._pinCoords = coords; }
  getPinCoordinates() { return this._pinCoords; }

  selectNode(node) {
    state.set('selectedNode', node);
    const kind = node?._kind;
    const useEndpoints = !!(node && ENDPOINT_KINDS.has(kind));
    const useStroke = !!(node && STROKE_KINDS.has(kind));
    if (useEndpoints) {
      this.transformer.nodes([]);
      this.endpointHandles.attach(node);
      this.strokeSelection.detach();
    } else if (useStroke) {
      this.transformer.nodes([]);
      this.endpointHandles.detach();
      this.strokeSelection.attach(node);
    } else {
      // Blur nodes have no stroke, and the user reads the selection rect as
      // "everything inside this box is blurred." The default 4px transformer
      // padding leaves an unblurred ring of the underlying screenshot
      // visible between the blur edge and the handle frame, which breaks
      // that mental model. Hug the node tight for blur; restore the
      // breathing room for shape annotations whose own stroke fills the gap.
      this.transformer.padding(kind === 'blur' ? 0 : 4);
      this.transformer.nodes(node ? [node] : []);
      this.endpointHandles.detach();
      this.strokeSelection.detach();
    }
    this.uiLayer.batchDraw();
  }

  deselect() {
    this.selectNode(null);
  }

  deleteSelection() {
    const node = state.get('selectedNode');
    if (!node) return;
    const parent = node.getParent();
    const index = node.zIndex();
    // Tools can stash satellite nodes on `_companions` (e.g. the highlight
    // tool's punch-out rect) so the engine removes/restores them in lockstep
    // with their owner. Without this, deleting a highlight leaves the scrim
    // cut-out behind, exposing a permanent "hole" in the dim overlay.
    const companions = Array.isArray(node._companions) ? node._companions : [];
    const companionState = companions
      .filter((c) => !!c)
      .map((c) => ({ node: c, parent: c.getParent(), index: c.zIndex() }))
      .filter((s) => !!s.parent);
    this.pushOp({
      undo: () => {
        parent.add(node); node.zIndex(index);
        companionState.forEach(({ node: c, parent: p, index: i }) => { p.add(c); c.zIndex(i); });
      },
      redo: () => {
        node.remove();
        companionState.forEach(({ node: c }) => c.remove());
      },
    });
    node.remove();
    companionState.forEach(({ node: c }) => c.remove());
    this.deselect();
    this.contentLayer.batchDraw();
  }

  // Convert a stage event into letterbox-relative coords (i.e. coords *inside*
  // the screenshot region) so tools can decide whether to ignore taps in the
  // empty letterbox bands.
  pointerInsideImage(pos) {
    const { ox, oy, dw, dh } = this.layout;
    return pos.x >= ox && pos.y >= oy && pos.x <= ox + dw && pos.y <= oy + dh;
  }

  async exportComposite({ maxEdge = 2560, quality = 0.88 } = {}) {
    const { naturalW, naturalH } = this.layout;
    const baseW = naturalW || this.layout.innerW;
    const baseH = naturalH || this.layout.innerH;
    const scale = Math.min(1, maxEdge / Math.max(baseW, baseH));
    const expW = Math.max(1, Math.round(baseW * scale));
    const expH = Math.max(1, Math.round(baseH * scale));

    // Hide the transformer and any UI overlays during export.
    this.transformer.visible(false);
    this.endpointHandles.setVisible(false);
    this.strokeSelection.setVisible(false);
    this.uiLayer.batchDraw();
    this.contentLayer.batchDraw();

    const dataUrl = this.stage.toDataURL({
      pixelRatio: expW / this.layout.innerW,
      mimeType: 'image/jpeg',
      quality,
    });

    this.transformer.visible(true);
    this.endpointHandles.setVisible(true);
    this.strokeSelection.setVisible(true);
    this.uiLayer.batchDraw();
    this.contentLayer.batchDraw();

    return await dataUrlToBlob(dataUrl);
  }

  destroy() {
    this.detachKeyboard();
    this.stage.off('pointerdown', this._onPointerDown);
    this.stage.off('pointermove', this._onPointerMove);
    this.stage.off('pointerup', this._onPointerUp);
    while (this._propUnsubs?.length) this._propUnsubs.pop()();
    this.endpointHandles.detach();
    this.strokeSelection.detach();
    this.stage.destroy();
    this._undoStack.length = 0;
    this._redoStack.length = 0;
    state.reset();
  }
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to decode image'));
    img.src = src;
  });
}

async function dataUrlToBlob(dataUrl) {
  const res = await fetch(dataUrl);
  return await res.blob();
}
